import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, Check, ExternalLink, MessageCircle } from 'lucide-react';

import { ContactSection } from '@/components/contact-section';
import { PublicCrossLinks } from '@/components/public-cross-links';
import { PublicSiteHeader } from '@/components/public-site-header';
import { canonicalSiteUrl } from '@/config';

import { whatsappUrl } from '@/lib/whatsapp';

const portfolioCases = [
  {
    title: '文仔清拆',
    slug: 'man-demolition',
    category: '清拆工程網站',
    url: 'https://mandemolition.com/',
    image: '/images/case-man-demolition.webp',
    alt: '文仔清拆網站首頁，展示香港住宅、寫字樓及商舖清拆工程服務',
    body: '香港清拆工程網站，集中展示住宅、寫字樓、商舖及餐廳清拆還原服務。以清晰報價、服務範圍和 WhatsApp 查詢入口，方便客人快速了解工程安排。',
  },
  {
    title: '文仔冷氣',
    slug: 'man-aircon',
    category: '本地服務網站',
    url: 'https://www.manaircon.xyz/',
    image: '/images/case-man-aircon.webp',
    alt: '文仔冷氣網站首頁截圖，展示冷氣安裝維修服務網站設計',
    body: '冷氣安裝、維修、清洗及保養服務網站。適合需要把服務範圍、工程案例、評價和 WhatsApp 查詢集中處理的本地服務公司。',
  },
  {
    title: '文仔水喉',
    slug: 'man-plumber',
    category: '維修服務網站',
    url: 'https://manplumber.com/',
    image: '/images/case-man-plumber.webp',
    alt: '文仔水喉網站首頁截圖，展示緊急水喉維修服務網站設計',
    body: '緊急水喉維修服務網站。適合想讓客人一入站就看到服務範圍、收費方向和聯絡方法的維修及上門服務生意。',
  },
  {
    title: '舒怡機電',
    slug: 'shu-yee-engineering',
    category: '公司網站重整',
    url: 'https://www.shuyee.xyz/',
    image: '/images/case-shuyee.webp',
    alt: '舒怡機電網站首頁截圖，展示公司網站重整設計',
    body: '公司網站製作及內容整理案例。適合舊網站內容太散、想重新整理公司形象、服務介紹和手機閱讀體驗的企業。',
  },
  {
    title: '正EV',
    slug: 'plus-ev',
    category: '電動車服務網站',
    url: 'https://plusevhk.com/',
    image: '/images/case-plus-ev.webp',
    alt: '正EV網站首頁截圖，展示電動車服務網站設計',
    body: '電動車相關服務網站案例。適合新服務或新品牌先把資料講清楚，再用網站承接搜尋、廣告或社交平台帶來的查詢。',
  },
];

const caseFaqs = [
  ['可以完全照抄其中一個案例的設計嗎？', '不會直接複製。可以參考資訊次序、功能和查詢做法，但視覺、內容和頁面結構應按你的品牌、服務和客人重新整理。'],
  ['可以指定某個案例作為報價參考嗎？', '可以。傳案例連結並指出你想參考的頁面、功能或內容深度，我們會再確認哪些部分適合你的情況。'],
  ['做類似案例是否一定同一個價錢？', '不一定。報價取決於頁數、內容、語言、CMS、功能和交付時間，不會單靠外觀相似就使用同一個價格。'],
  ['案例截圖是網站目前畫面嗎？', '案例卡使用實際網站截圖並提供原站連結。網站由客戶持續營運，日後內容或版面可能更新，所以原站畫面可能與截圖略有不同。'],
  ['已有舊網站，可以只重整部分頁面嗎？', '可以。可先盤點現有內容，保留仍有價值的頁面和搜尋基礎，優先改善主頁、重點服務頁、手機體驗及查詢路徑。'],
];

export const Route = createFileRoute('/cases')({
  head: () => {
    const baseUrl = canonicalSiteUrl;
    const pageUrl = `${baseUrl}/cases`;
    const ogImageUrl = `${baseUrl}/og-image.jpg`;
    const title = '網站案例｜渡橋 Doq Creative';
    const description = '查看渡橋 Doq Creative 過往網站案例。如果你想做類似的公司網站、本地服務網站或 Landing Page，可以先參考不同類型的頁面做法，再同我們度一度網站方向。';
    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: pageUrl },
        { property: 'og:image', content: ogImageUrl },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { property: 'og:image:alt', content: '渡橋 Doq Creative 香港網頁設計與 SEO 預覽圖' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: ogImageUrl },
        { name: 'twitter:image:alt', content: '渡橋 Doq Creative 香港網頁設計與 SEO 預覽圖' },
        {
          'script:ld+json': {
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'CollectionPage',
                '@id': `${pageUrl}#collection`,
                name: '網站案例',
                url: pageUrl,
                inLanguage: 'zh-HK',
                isPartOf: { '@type': 'WebSite', '@id': `${baseUrl}/#website` },
                about: {
                  '@type': 'Service',
                  name: '網頁設計',
                  provider: {
                    '@type': 'ProfessionalService',
                    '@id': `${baseUrl}/#organization`,
                    name: '渡橋 Doq Creative',
                  },
                },
                mainEntity: portfolioCases.map((caseItem) => ({
                  '@type': 'CreativeWork',
                  name: caseItem.title,
                  url: caseItem.url,
                  image: `${baseUrl}${caseItem.image}`,
                  description: caseItem.body,
                })),
              },
              {
                '@type': 'BreadcrumbList',
                '@id': `${pageUrl}#breadcrumb`,
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: '首頁', item: `${baseUrl}/` },
                  { '@type': 'ListItem', position: 2, name: '案例', item: pageUrl },
                ],
              },
              {
                '@type': 'FAQPage',
                '@id': `${pageUrl}#faq`,
                inLanguage: 'zh-HK',
                mainEntity: caseFaqs.map(([q, a]) => ({
                  '@type': 'Question',
                  name: q,
                  acceptedAnswer: { '@type': 'Answer', text: a },
                })),
              },
            ],
          },
        },
      ],
      links: [
        { rel: 'canonical', href: pageUrl },
        { rel: 'alternate', hrefLang: 'zh-HK', href: pageUrl },
        { rel: 'alternate', hrefLang: 'en', href: `${baseUrl}/en/cases` },
        { rel: 'alternate', hrefLang: 'x-default', href: pageUrl },
      ],
    };
  },
  component: CasesPage,
});

function CasesPage() {
  return (
    <div className="min-h-screen bg-[#f4efe4] text-[#102d28]" style={{ overflowAnchor: 'none' }}>
      <div className="mx-auto max-w-[1540px] border-x border-[#143d35]/16 bg-[#f8f3e9] shadow-[0_0_0_1px_rgba(20,61,53,0.04)]">
        <PublicSiteHeader locale="zh" active="cases" enHref="/en/cases" zhHref="/cases" />

        <main id="main-content">
          <section className="relative overflow-hidden border-b border-[#143d35]/14 px-5 py-16 sm:px-8 lg:px-24 lg:py-20">
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(20,61,53,0.07)_1px,transparent_1px),linear-gradient(to_bottom,rgba(20,61,53,0.07)_1px,transparent_1px)] bg-[size:58px_58px]" />
            <div className="relative z-10 max-w-4xl">
              <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-[#173d35] hover:text-[#815321]">
                <ArrowLeft className="size-4" />
                返回首頁
              </Link>
              <p className="mt-12 text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">Selected work</p>
              <h1 className="mt-5 max-w-3xl text-balance text-4xl font-semibold leading-[1.12] tracking-[-0.06em] text-[#063d36] sm:text-5xl lg:text-6xl">
                參考一下，哪種網站做法比較適合你
              </h1>
              <p className="mt-8 max-w-2xl text-lg leading-9 tracking-[-0.02em] text-[#324740]">
                如果你想重做公司網站、做本地服務網站，或者先用一頁 Landing Page 試市場，可以先看看這幾個項目的方向。覺得有接近你生意的做法，就直接 WhatsApp 我們，我哋可以幫你度下一步應該點做。
              </p>
            </div>
          </section>

          <section className="border-b border-[#143d35]/14 px-5 py-12 sm:px-8 lg:px-24">
            <div className="grid gap-6 md:grid-cols-2">
              {portfolioCases.map((item) => (
                <article key={item.title} className="group border border-[#143d35]/14 bg-[#fbf7ef]">
                  <a
                    href={`/case/${item.slug}`}
                    aria-label={`查看${item.title}案例詳情`}
                    className="block overflow-hidden bg-[#efe8dc]"
                  >
                    <img
                      src={item.image}
                      alt={item.alt}
                      width="1400"
                      height="900"
                      className="aspect-[16/10] w-full object-cover object-top transition duration-500 group-hover:scale-[1.015]"
                      loading="eager"
                    />
                  </a>
                  <div className="p-7 sm:p-9">
                    <p className="text-sm font-semibold text-[#815321]">{item.category}</p>
                    <div className="mt-3 flex items-start justify-between gap-4">
                      <a href={`/case/${item.slug}`} className="text-3xl font-semibold tracking-[-0.055em] text-[#102d28] hover:text-[#815321]">{item.title}</a>
                      <a
                        href={`/case/${item.slug}`}
                        className="grid size-10 shrink-0 place-items-center border border-[#143d35]/18 text-[#143d35] transition hover:bg-[#063d36] hover:text-white"
                        aria-label={`查看${item.title}案例詳情`}
                      >
                        <ExternalLink className="size-4" />
                      </a>
                    </div>
                    <p className="mt-5 max-w-2xl text-sm leading-7 text-[#344840]">{item.body}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="border-b border-[#143d35]/14 px-5 py-12 sm:px-8 lg:px-24 lg:py-16"><div className="grid gap-8 lg:grid-cols-[0.72fr_1.28fr]"><div><h2 className="text-3xl font-semibold tracking-[-0.04em]">三類案例，應該看甚麼？</h2><p className="mt-5 max-w-xl text-sm leading-7 text-[#344840]">案例的價值不是讓你挑一個外觀照搬，而是比較不同行業如何安排資料、建立信任和引導查詢。</p></div><div className="overflow-x-auto"><table className="w-full min-w-[620px] text-left text-sm"><thead className="border-b-2 border-[#143d35]"><tr><th className="py-3 pr-5">類型</th><th className="py-3 pr-5">訪客最關心</th><th className="py-3">頁面重點</th></tr></thead><tbody className="divide-y divide-[#143d35]/14 text-[#344840]"><tr><td className="py-4 pr-5 font-semibold text-[#102d28]">本地上門服務</td><td className="py-4 pr-5">地區、服務範圍、回覆速度</td><td className="py-4">電話、WhatsApp、價錢方向</td></tr><tr><td className="py-4 pr-5 font-semibold text-[#102d28]">專業 / B2B 服務</td><td className="py-4 pr-5">經驗、流程、合作可信度</td><td className="py-4">公司背景、案例、交付方式</td></tr><tr><td className="py-4 pr-5 font-semibold text-[#102d28]">新品牌 / 新服務</td><td className="py-4 pr-5">這項服務是甚麼、是否適合</td><td className="py-4">清楚定位、單一 CTA、流量承接</td></tr></tbody></table></div></div></section>
          <section className="border-b border-[#143d35]/14 px-5 py-12 sm:px-8 lg:px-24 lg:py-16"><h2 className="text-3xl font-semibold tracking-[-0.04em]">看案例時，用這五點判斷</h2><ul className="mt-8 grid gap-px bg-[#143d35]/14 sm:grid-cols-2 lg:grid-cols-5">{['首屏能否講清楚做甚麼', '服務是否按客人問題分類', '信任資料是否具體易找', '手機版查詢是否順手', '是否有下一步 CTA'].map((item) => <li key={item} className="flex gap-3 bg-[#fbf7ef] p-5 text-sm leading-6"><Check className="mt-0.5 size-4 shrink-0 text-[#815321]" />{item}</li>)}</ul></section>
          <PublicCrossLinks current="cases" />
          <section id="faq" className="border-b border-[#143d35]/14 px-5 py-12 sm:px-8 lg:px-24 lg:py-16"><h2 className="text-3xl font-semibold tracking-[-0.04em]">網站案例 FAQ</h2><div className="mt-7 divide-y divide-[#143d35]/14 border-y border-[#143d35]/14">{caseFaqs.map(([q, a]) => <details key={q} className="py-5"><summary className="cursor-pointer list-none pr-8 font-semibold marker:content-none">{q}</summary><p className="mt-3 max-w-3xl text-sm leading-7 text-[#344840]">{a}</p></details>)}</div></section>
          <ContactSection
            locale="zh"
            title="想做類似網站？先同我哋度一度"
            description="如果入面有接近你行業或做法的案例，可以直接傳俾我哋參考。講低你想推的服務、現有網站情況和預算方向，我哋會先幫你判斷應該做公司網站、服務頁，還是先做一頁 Landing Page。"
          />
        </main>
        <footer className="border-t border-[#143d35]/14 px-5 py-6 text-center text-sm text-[#344840] sm:px-8 lg:px-24"><div className="flex flex-wrap justify-center gap-x-5 gap-y-2"><Link to="/" className="font-semibold">渡橋創意有限公司</Link><Link to="/terms">服務條款</Link><Link to="/privacy">隱私政策</Link></div><p className="mt-3">© 2026 渡橋創意有限公司。保留所有權利。</p></footer>
      </div>
      <FloatingWhatsApp />
    </div>
  );
}

function FloatingWhatsApp() {
  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-5 right-5 z-50 grid size-14 place-items-center rounded-full bg-[#063d36] text-[#fbf5ea] shadow-[0_16px_34px_rgba(6,61,54,0.28)] transition hover:-translate-y-1 hover:bg-[#0b4f45]"
      aria-label="WhatsApp 查詢"
    >
      <MessageCircle className="size-6" strokeWidth={1.8} />
    </a>
  );
}
